import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../../commonDesign/Button'
import Icon from '../../../../../../atoms/Icon'
import DenaliLogo from '../../../../../../../apps/aiApp/Assistant/common/DenaliLogo'
import ApprovalCard from '../blocks/ApprovalCard'
import { useAgentPanelContext } from '../AgentPanelContext'
import { useRotatingMessage } from '../hooks/useRotatingMessage'
import ChatMessage from './ChatMessage'

const MAX_MESSAGE_LENGTH = 4000
const GENERIC_MESSAGES = new Set(['Queued request...', 'Thinking...'])

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: 'linear-gradient(var(--color-ai-050), var(--color-ai-100))',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif",
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    color: 'var(--color-neutral-800)'
  },
  messageList: {
    flex: 1,
    overflow: 'auto',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  thinkingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    fontSize: '11.5px',
    border: '1px solid var(--color-ai-200, rgba(169,88,255,0.18))',
    background: 'rgba(255, 255, 255, 0.5)',
    color: 'var(--color-ai-700)',
    borderRadius: '6px'
  },
  thinkingLogo: {
    width: '18px',
    height: '18px',
    flexShrink: 0,
    animation: '$logoSpin 1.2s linear infinite'
  },
  thinkingText: {
    transition: 'opacity 0.3s ease'
  },
  '@keyframes logoSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  },
  inputArea: {
    borderTop: '1px solid var(--color-ai-200, rgba(169, 88, 255, 0.2))',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  textarea: {
    width: '100%',
    minHeight: '56px',
    maxHeight: '120px',
    border: '1px solid var(--color-ai-300, rgba(169, 88, 255, 0.3))',
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '12.5px',
    lineHeight: 1.5,
    resize: 'vertical',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    color: 'var(--color-neutral-800)',
    '&:focus': {
      outline: 'none',
      borderColor: 'var(--color-ai-400)',
      backgroundColor: 'rgba(255, 255, 255, 0.92)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  inputFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  charCount: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    fontVariantNumeric: 'tabular-nums'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: '8px',
    color: 'var(--color-neutral-500)',
    fontSize: '12.5px',
    textAlign: 'center',
    padding: '24px'
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    background: 'var(--color-danger-50, #fef2f2)',
    border: '1px solid var(--color-danger-200, #fecaca)',
    borderRadius: '8px',
    color: 'var(--color-danger-700, #b91c1c)',
    fontSize: '12px',
    lineHeight: '1.4'
  },
  errorIcon: {
    flexShrink: 0,
    fontSize: '14px'
  }
}))

function ChatPanel () {
  const classes = useStyles()
  const {
    addMessage,
    cancelThread,
    isCancelling,
    resumeThread,
    messages,
    agentStatus,
    isPendingAction,
    isResuming,
    interrupts,
    processingMessage,
    isThreadLoading,
    isErrored,
    config,
    onApplySource,
    onApplyTest,
    autoAccept,
    currentSource,
    isEditorMode,
    testsRef
  } = useAgentPanelContext()

  const [input, setInput] = useState('')
  const messagesEndRef = useRef(null)
  const isProcessing = agentStatus === 'processing' || isCancelling
  const isBusy = isProcessing || isPendingAction
  const showThinking = isProcessing || isThreadLoading
  const toolOverride = processingMessage && !GENERIC_MESSAGES.has(processingMessage)
    ? processingMessage
    : null
  const displayMessage = useRotatingMessage(showThinking, toolOverride)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, processingMessage])

  const handleSend = useCallback(async () => {
    const trimmed = input.trim()
    if (!trimmed || isProcessing) return
    setInput('')
    try {
      await addMessage(trimmed)
    } catch {
      /* error handled by hook */
    }
  }, [input, isProcessing, addMessage])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  const handleApprove = useCallback(async (feedback) => {
    try { await resumeThread('approve', feedback) } catch { /* handled by hook */ }
  }, [resumeThread])

  const handleReject = useCallback(async (feedback) => {
    try { await resumeThread('reject', feedback) } catch { /* handled by hook */ }
  }, [resumeThread])

  const visibleMessages = messages.filter(m => m.role === 'user' || m.role === 'assistant' || m.role === 'compaction')
  const placeholder = isEditorMode
    ? (config.editPlaceholder || 'Describe what you want to change...')
    : (config.placeholder || 'Describe what you want to build...')

  return (
    <div className={classes.container}>
      <div className={classes.messageList}>
        {visibleMessages.length === 0 && !isThreadLoading && (
          <div className={classes.emptyState}>
            <span style={{ fontSize: '24px' }}>✨</span>
            <span>Ask the designer to help you build or modify a smart endpoint.</span>
          </div>
        )}
        {visibleMessages.map((msg, idx) => (
          <ChatMessage
            key={msg.id || `msg-${idx}`}
            message={msg}
            onApplySource={onApplySource}
            onApplyTest={onApplyTest}
            autoAccept={autoAccept}
            currentSource={currentSource}
            existingTests={testsRef?.current || []}
            allMessages={messages}
          />
        ))}
        {isPendingAction && interrupts.length > 0 && (
          <ApprovalCard
            interrupt={interrupts[0]}
            onApprove={handleApprove}
            onReject={handleReject}
            isProcessing={isResuming}
          />
        )}
        {isErrored && !showThinking && (
          <div className={classes.errorBanner}>
            <span className={classes.errorIcon}>⚠</span>
            <span>Something went wrong. Please try again or refresh to start a new session.</span>
          </div>
        )}
        {showThinking && (
          <div className={classes.thinkingIndicator}>
            <div className={classes.thinkingLogo}>
              <DenaliLogo size='18px' alt='Processing' />
            </div>
            <span className={classes.thinkingText}>{displayMessage}</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={classes.inputArea}>
        <textarea
          className={classes.textarea}
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isPendingAction}
          aria-label='Message to designer'
        />
        <div className={classes.inputFooter}>
          <span className={classes.charCount}>{input.length}/{MAX_MESSAGE_LENGTH}</span>
          <SydButton
            size='sm'
            variant={isProcessing ? 'outline' : 'ai'}
            onClick={isProcessing ? cancelThread : handleSend}
            disabled={isProcessing ? isCancelling : (!input.trim() || isBusy)}
          >
            {isProcessing
              ? <Icon name='square' style={{ fontSize: '16px' }} />
              : 'Send'}
          </SydButton>
        </div>
      </div>
    </div>
  )
}

export default ChatPanel
